'use client'

import { useEffect, useRef, type CSSProperties, type ReactNode } from 'react'
import { Disc3 } from 'lucide-react'
import { CoverImg } from '@/components/ui/kit'
import Artwork from '@/components/Artwork'
import type { PlayerTrack } from '@/lib/player-context'
import { clamp, fxDisabled, lowEndDevice } from './fx-utils'

/**
 * POCHETTE 3D « waouh » — vraie boîte en CSS 3D (sans librairie) :
 *  - face avant = la pochette, face arrière = verso (jaquette floutée + infos),
 *    tranche épaisse (dos + haut + bas), côté droit OUVERT d'où sort le vinyle ;
 *  - suit la souris (desktop) ou le gyroscope (mobile, si autorisé), sinon
 *    légère rotation automatique ; reflet spéculaire + ombre portée dynamiques ;
 *  - glisser horizontalement = retourner la pochette (voir le verso), clic /
 *    Entrée = `onToggle` (lecture/pause) ;
 *  - `out` : le vinyle glisse hors de la pochette ; `spinning` : il tourne.
 * Désactivée proprement (pose statique, pas de boucle) en mode TV / fx-off /
 * « réduire les animations ». Seuls transform/opacity sont animés.
 */
type Variant = 'black' | 'white'
type Props = {
  size: number                         // côté de la pochette (px)
  src?: string                         // image (sinon `videoId`, sinon `art`)
  videoId?: string
  /** Titre musical : sa VRAIE pochette (album/single) remplace toute image tirée d'une vidéo YouTube. */
  track?: PlayerTrack | null
  /** Rendu personnalisé de l'image (ex. <Artwork/> du lecteur) : px = taille, round = étiquette ronde du disque. */
  art?: (px: number, round: boolean) => ReactNode
  title?: string
  subtitle?: string
  out?: boolean                        // vinyle sorti
  spinning?: boolean                   // vinyle qui tourne
  variant?: Variant
  onToggle?: () => void
  /** Arrivée en vol 3D au montage (ouverture d'un album). */
  intro?: boolean
  /** Réserve la place du disque sorti et recentre l'ensemble (défaut : true). */
  reserve?: boolean
  className?: string
  toggleLabel?: string
}

const OUT = 0.5   // part du côté de la pochette dont le vinyle dépasse

let styleInjected = false
const KEYFRAMES = `
@keyframes blcCoverFly {
  0%   { opacity: 0; transform: translate3d(-18%, -30%, -700px) rotateY(-140deg) rotateX(35deg) rotateZ(-12deg); }
  60%  { opacity: 1; }
  100% { opacity: 1; transform: none; }
}
@keyframes blcGlowPulse { 0%,100% { opacity: .55; transform: translateZ(-90px) scale(1); } 50% { opacity: .9; transform: translateZ(-90px) scale(1.08); } }
`

export default function Cover3D({
  size, src, videoId, track, art, title, subtitle, out = false, spinning = false, variant = 'black',
  onToggle, intro = false, reserve = true, className = '', toggleLabel,
}: Props) {
  const rootRef = useRef<HTMLDivElement>(null)
  const rigRef = useRef<HTMLDivElement>(null)
  const glareRef = useRef<HTMLDivElement>(null)
  const shadowRef = useRef<HTMLDivElement>(null)
  const toggleRef = useRef(onToggle)
  toggleRef.current = onToggle

  const D = Math.max(7, Math.round(size * 0.045))        // épaisseur de la tranche
  const room = reserve ? Math.round(size * OUT) : 0       // place réservée au disque sorti
  const white = variant === 'white'
  const disc = Math.round(size * 0.94)
  const label = Math.round(disc * 0.4)

  // ---------- Boucle d'animation (souris / gyroscope / auto / glisser) ----------
  useEffect(() => {
    const root = rootRef.current, rig = rigRef.current
    if (!root || !rig) return
    const off = fxDisabled()
    const st = {
      tx: 0, ty: 0, cx: 0, cy: 0, flip: 0, flipCur: 0,
      lastInput: -1e9, drag: null as null | { x: number; flip: number; moved: boolean; id: number },
      visible: true, raf: 0, gyroAsked: false,
    }

    const apply = () => {
      rig.style.transform = `rotateX(${st.cy.toFixed(2)}deg) rotateY(${(st.cx + st.flipCur).toFixed(2)}deg)`
      const g = glareRef.current
      if (g) {
        g.style.transform = `translate3d(${(-st.cx * 1.5).toFixed(1)}%, ${(st.cy * 2).toFixed(1)}%, 0)`
        g.style.opacity = String(clamp(0.35 + Math.abs(st.cx) / 45 + Math.abs(st.cy) / 60, 0, 0.95))
      }
      const s = shadowRef.current
      if (s) s.style.transform = `translateX(${(st.cx * 1.3).toFixed(1)}px) scaleX(${(1 - Math.abs(st.cy) / 80).toFixed(3)})`
    }

    if (off) {
      st.cx = -16; st.cy = 5; apply()
    }

    const frame = (now: number) => {
      st.raf = 0
      if (!st.drag && now - st.lastInput > 2600) {
        const t = now / 1000                       // rotation automatique douce
        st.tx = Math.sin(t * 0.55) * 18 - 6
        st.ty = Math.sin(t * 0.37) * 6 + 3
      }
      st.cx += (st.tx - st.cx) * 0.075
      st.cy += (st.ty - st.cy) * 0.075
      st.flipCur += (st.flip - st.flipCur) * (st.drag ? 0.35 : 0.09)
      apply()
      if (st.visible) st.raf = requestAnimationFrame(frame)
    }
    const start = () => { if (!off && !st.raf && st.visible) st.raf = requestAnimationFrame(frame) }

    // Souris : l'objet regarde le pointeur (toute la fenêtre).
    const fine = (() => { try { return window.matchMedia('(pointer: fine)').matches } catch { return false } })()
    const onMove = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse' || st.drag) return
      const r = root.getBoundingClientRect()
      const nx = clamp((e.clientX - (r.left + r.width / 2)) / (window.innerWidth / 2), -1, 1)
      const ny = clamp((e.clientY - (r.top + r.height / 2)) / (window.innerHeight / 2), -1, 1)
      st.tx = nx * 26; st.ty = -ny * 16
      st.lastInput = performance.now()
    }
    // Gyroscope (Android direct ; iOS après autorisation au 1er toucher).
    const onOri = (e: DeviceOrientationEvent) => {
      if (e.gamma == null || st.drag) return
      st.tx = clamp(e.gamma, -35, 35) * 0.8
      st.ty = clamp((e.beta ?? 45) - 45, -30, 30) * -0.55
      st.lastInput = performance.now()
    }
    const DOE: any = typeof window !== 'undefined' ? (window as any).DeviceOrientationEvent : undefined
    const needsPerm = !!DOE && typeof DOE.requestPermission === 'function'
    if (!off) {
      if (fine) window.addEventListener('pointermove', onMove, { passive: true })
      else if (DOE && !needsPerm && !lowEndDevice()) window.addEventListener('deviceorientation', onOri, { passive: true })
    }
    const askGyro = () => {
      if (off || fine || !needsPerm || st.gyroAsked) return
      st.gyroAsked = true
      DOE.requestPermission().then((r: string) => { if (r === 'granted') window.addEventListener('deviceorientation', onOri, { passive: true }) }).catch(() => {})
    }

    // Glisser = retourner la pochette ; simple clic = lecture/pause.
    const onDown = (e: PointerEvent) => {
      if (e.button !== 0) return
      st.drag = { x: e.clientX, flip: st.flip, moved: false, id: e.pointerId }
      start()
    }
    const onDragMove = (e: PointerEvent) => {
      const d = st.drag
      if (!d || e.pointerId !== d.id) return
      const dx = e.clientX - d.x
      if (!d.moved && Math.abs(dx) > 7) {
        d.moved = true
        try { root.setPointerCapture(e.pointerId) } catch {}
      }
      if (d.moved && !off) st.flip = d.flip + dx * 0.6
    }
    const onUp = (e: PointerEvent) => {
      const d = st.drag
      if (!d || e.pointerId !== d.id) return
      st.drag = null
      try { root.releasePointerCapture(e.pointerId) } catch {}
      if (!d.moved) { askGyro(); toggleRef.current?.() }
      else st.flip = Math.round(st.flip / 180) * 180
      st.lastInput = performance.now()
    }
    const onCancel = () => { if (st.drag) { st.drag = null; st.flip = Math.round(st.flip / 180) * 180 } }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleRef.current?.() }
      else if (!off && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
        e.preventDefault(); st.flip += e.key === 'ArrowLeft' ? -180 : 180; st.lastInput = performance.now(); start()
      }
    }
    root.addEventListener('pointerdown', onDown)
    root.addEventListener('pointermove', onDragMove)
    root.addEventListener('pointerup', onUp)
    root.addEventListener('pointercancel', onCancel)
    root.addEventListener('keydown', onKey)

    // Boucle active seulement quand la pochette est à l'écran.
    const io = new IntersectionObserver(([en]) => {
      st.visible = !!en?.isIntersecting
      if (st.visible) start()
    })
    io.observe(root)
    start()

    return () => {
      io.disconnect()
      if (st.raf) cancelAnimationFrame(st.raf)
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('deviceorientation', onOri)
      root.removeEventListener('pointerdown', onDown)
      root.removeEventListener('pointermove', onDragMove)
      root.removeEventListener('pointerup', onUp)
      root.removeEventListener('pointercancel', onCancel)
      root.removeEventListener('keydown', onKey)
    }
  }, [])

  // Injecte les @keyframes une seule fois (pas d'accès à globals.css).
  useEffect(() => {
    if (styleInjected || typeof document === 'undefined') return
    styleInjected = true
    const el = document.createElement('style')
    el.setAttribute('data-blc-fx', 'cover3d')
    el.textContent = KEYFRAMES
    document.head.appendChild(el)
  }, [])

  const renderArt = (px: number, round: boolean) => {
    if (art) return art(px, round)
    // Image « vraie pochette » (non YouTube) fournie → on la garde.
    if (src && !/ytimg\.com|youtube\.com/.test(src)) return <CoverImg src={src} />
    // Titre musical → vraie pochette carrée, jamais l'image de la vidéo.
    if (track) return <span className="absolute inset-0"><Artwork track={track} fill strict size={px} rounded="rounded-none" /></span>
    if (src || videoId) return <CoverImg src={src} videoId={videoId} />
    return (
      <span className="absolute inset-0 grid place-items-center bg-gradient-to-br from-a1 to-adeep">
        <Disc3 size={Math.round(px * 0.4)} className="text-white/80" />
      </span>
    )
  }

  const face: CSSProperties = { position: 'absolute', backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden' }
  const p3d: CSSProperties = { transformStyle: 'preserve-3d' }
  const flyOn = intro && typeof window !== 'undefined' && !fxDisabled()

  return (
    <div
      ref={rootRef}
      className={`relative select-none outline-none focus-visible:ring-2 focus-visible:ring-a3/70 rounded-xl ${className}`}
      role={onToggle ? 'button' : undefined}
      tabIndex={onToggle ? 0 : undefined}
      aria-label={toggleLabel ?? (onToggle ? (spinning ? 'Mettre en pause' : 'Lancer la musique') : undefined)}
      title={onToggle ? `${spinning ? 'Pause' : 'Lecture'} · glisser pour retourner la pochette` : 'Glisser pour retourner la pochette'}
      style={{
        width: size + room, height: size,
        perspective: Math.round(size * 3.4), perspectiveOrigin: '50% 45%',
        touchAction: 'pan-y', cursor: onToggle ? 'pointer' : 'grab',
      }}
    >
      {/* Glissière : recentre l'ensemble selon que le disque est sorti ou non. */}
      <div style={{
        ...p3d, position: 'absolute', left: 0, top: 0, width: size, height: size,
        transform: `translateX(${out ? 0 : room / 2}px)`,
        transition: 'transform .9s cubic-bezier(.22,1,.36,1)',
      }}>
        {/* Ombre portée (suit l'inclinaison). */}
        <div ref={shadowRef} aria-hidden style={{
          position: 'absolute', left: '4%', width: out ? '140%' : '92%', top: '96%', height: size * 0.16,
          background: 'radial-gradient(50% 50% at 50% 50%, rgba(0,0,0,.55), rgba(0,0,0,0) 70%)',
          transition: 'width .9s cubic-bezier(.22,1,.36,1)', pointerEvents: 'none', willChange: 'transform',
        }} />
        {/* Halo d'accent derrière (respire quand ça joue). */}
        <div aria-hidden style={{
          position: 'absolute', inset: '-12%', borderRadius: '50%', pointerEvents: 'none',
          background: 'radial-gradient(closest-side, rgb(var(--a2)/.55), rgb(var(--a1)/.18) 55%, transparent 75%)',
          transform: 'translateZ(-90px)', opacity: 0.6,
          animation: spinning ? 'blcGlowPulse 1.9s ease-in-out infinite' : undefined,
        }} />

        {/* Arrivée en vol (optionnelle). */}
        <div style={{ ...p3d, position: 'absolute', inset: 0, animation: flyOn ? 'blcCoverFly 1.05s cubic-bezier(.2,.8,.2,1) both' : undefined }}>
          {/* RIG : la rotation 3D pilotée par la boucle. */}
          <div ref={rigRef} style={{ ...p3d, position: 'absolute', inset: 0, willChange: 'transform', transform: 'rotateX(4deg) rotateY(-14deg)' }}>

            {/* VINYLE (au milieu de l'épaisseur, sort par le côté droit ouvert). */}
            <div style={{
              position: 'absolute', left: (size - disc) / 2, top: (size - disc) / 2, width: disc, height: disc,
              transform: `translate3d(${out ? Math.round(size * OUT) : 0}px, 0, 0)`,
              transition: 'transform .95s cubic-bezier(.22,1,.36,1)',
            }}>
              <div style={{
                width: '100%', height: '100%', borderRadius: '50%', display: 'grid', placeItems: 'center',
                background: white
                  ? 'linear-gradient(135deg, rgba(255,255,255,.55), rgba(255,255,255,0) 30%, rgba(0,0,0,.05) 70%, rgba(255,255,255,.35)), repeating-radial-gradient(circle at 50% 50%, #dcdce2 0px, #dcdce2 1px, #f6f6f9 2px, #dcdce2 3px), radial-gradient(circle, #fff 0%, #ececf1 58%, #d9d9e0 100%)'
                  : 'linear-gradient(135deg, rgba(255,255,255,.12), rgba(255,255,255,0) 30%, rgba(255,255,255,0) 70%, rgba(255,255,255,.07)), repeating-radial-gradient(circle at 50% 50%, #0b0b0f 0px, #0b0b0f 1px, #17171d 2px, #0b0b0f 3px), radial-gradient(circle, #1a1a22 0%, #050507 60%, #000 100%)',
                boxShadow: white ? '0 0 0 1px rgba(0,0,0,.1) inset, 0 10px 30px rgba(0,0,0,.35)' : '0 0 0 1px rgba(255,255,255,.07) inset, 0 10px 30px rgba(0,0,0,.6)',
                animation: 'spinSlow 3.4s linear infinite',
                animationPlayState: spinning ? 'running' : 'paused',
              }}>
                <div style={{
                  position: 'relative', width: label, height: label, borderRadius: '50%', overflow: 'hidden',
                  boxShadow: white ? '0 0 0 4px #f0f0f4, 0 0 0 5px rgba(0,0,0,.12)' : '0 0 0 4px #0a0a0d, 0 0 0 5px rgba(255,255,255,.12)',
                }}>
                  {renderArt(label, true)}
                  <span style={{
                    position: 'absolute', left: '50%', top: '50%', width: Math.max(8, disc * 0.035), height: Math.max(8, disc * 0.035),
                    transform: 'translate(-50%,-50%)', borderRadius: '50%',
                    background: white ? 'radial-gradient(circle, #d9d9e0 40%, #f6f6f9)' : 'radial-gradient(circle, #000 40%, #222)',
                  }} />
                </div>
                {/* reflet fixe qui « balaie » le disque en tournant */}
                <span style={{
                  position: 'absolute', inset: 0, borderRadius: '50%', pointerEvents: 'none',
                  background: `conic-gradient(from 210deg, rgba(255,255,255,0) 0deg, rgba(255,255,255,${white ? '.5' : '.09'}) 25deg, rgba(255,255,255,0) 60deg, rgba(255,255,255,0) 360deg)`,
                }} />
              </div>
            </div>

            {/* FACE AVANT */}
            <div style={{ ...face, inset: 0, overflow: 'hidden', borderRadius: 6, transform: `translateZ(${D / 2}px)`,
              boxShadow: 'inset 0 0 0 1px rgba(255,255,255,.10)', background: 'rgb(var(--spanel3))' }}>
              {renderArt(size, false)}
              {/* grain carton + liseré d'ouverture à droite */}
              <span aria-hidden style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, rgba(0,0,0,0) 94%, rgba(0,0,0,.35))', pointerEvents: 'none' }} />
              {/* reflet spéculaire (grand calque déplacé en transform = GPU) */}
              <div ref={glareRef} aria-hidden style={{
                position: 'absolute', left: '-50%', top: '-50%', width: '200%', height: '200%', pointerEvents: 'none',
                background: 'radial-gradient(closest-side at 50% 50%, rgba(255,255,255,.34), rgba(255,255,255,.08) 45%, rgba(255,255,255,0) 70%)',
                mixBlendMode: 'soft-light', willChange: 'transform, opacity',
              }} />
              <span aria-hidden style={{ position: 'absolute', inset: 0, background: 'linear-gradient(115deg, rgba(255,255,255,.16) 0%, rgba(255,255,255,0) 32%)', pointerEvents: 'none' }} />
            </div>

            {/* FACE ARRIÈRE (verso) */}
            <div className="on-media" style={{ ...face, inset: 0, overflow: 'hidden', borderRadius: 6, transform: `rotateY(180deg) translateZ(${D / 2}px)`, background: 'rgb(var(--adeep))' }}>
              <div style={{ position: 'absolute', inset: 0, filter: 'blur(18px) saturate(1.3)', transform: 'scale(1.25)', opacity: 0.55 }}>
                {renderArt(size, false)}
              </div>
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(160deg, rgba(0,0,0,.25), rgba(0,0,0,.75))' }} />
              <div style={{ position: 'absolute', inset: 0, padding: size * 0.08, display: 'flex', flexDirection: 'column', color: '#fff' }}>
                <div style={{ fontSize: Math.max(9, size * 0.035), letterSpacing: '.28em', fontWeight: 700, opacity: 0.7 }}>BLC MÉDIAS</div>
                <div style={{ marginTop: size * 0.04, fontSize: Math.max(13, size * 0.075), fontWeight: 800, lineHeight: 1.08, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' as any }}>{title || 'Sans titre'}</div>
                {subtitle && <div style={{ marginTop: 4, fontSize: Math.max(11, size * 0.045), opacity: 0.75, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{subtitle}</div>}
                <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 8 }}>
                  <div style={{ fontSize: Math.max(8, size * 0.028), opacity: 0.55, lineHeight: 1.4 }}>Face A · 33⅓ tr/min<br />Stéréo · ℗ 2026</div>
                  {/* faux code-barres */}
                  <div aria-hidden style={{ width: size * 0.26, height: size * 0.12, background: '#fff', borderRadius: 2, padding: 3 }}>
                    <div style={{ width: '100%', height: '100%', background: 'repeating-linear-gradient(90deg, #000 0 2px, transparent 2px 3px, #000 3px 4px, transparent 4px 6px, #000 6px 9px, transparent 9px 10px)' }} />
                  </div>
                </div>
              </div>
            </div>

            {/* TRANCHE : dos (gauche), haut, bas — côté droit ouvert. */}
            <div aria-hidden style={{ ...face, left: (size - D) / 2, top: 0, width: D, height: size, transform: `rotateY(-90deg) translateZ(${size / 2}px)`,
              background: 'linear-gradient(180deg, rgb(var(--a1)), rgb(var(--adeep)))' }} />
            <div aria-hidden style={{ ...face, left: 0, top: (size - D) / 2, width: size, height: D, transform: `rotateX(90deg) translateZ(${size / 2}px)`,
              background: 'linear-gradient(90deg, rgb(var(--adeep)), rgb(var(--a2)) 80%, #000)' }} />
            <div aria-hidden style={{ ...face, left: 0, top: (size - D) / 2, width: size, height: D, transform: `rotateX(-90deg) translateZ(${size / 2}px)`,
              background: 'linear-gradient(90deg, rgb(var(--adeep)), rgb(var(--a1)) 80%, #000)' }} />
          </div>
        </div>
      </div>
    </div>
  )
}
